import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, DatePicker, Space, Select, Spin, Alert, Tag } from 'antd';
import Table from 'components/Common/Table';
import { UserOutlined, CheckCircleOutlined, PercentageOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import axios from 'axios';
import useIsMobile from '../../hooks/useIsMobile';
import ReportFilterModal, { DatePresetButtons, MobileFilterButton, getDateRangeFromPreset } from '../../components/Reports/ReportFilterModal';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import useUrlFilters from '../../hooks/useUrlFilters';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const COLORS = ['#52c41a', '#1890ff', '#722ed1', '#fa541c', '#faad14', '#13c2c2', '#eb2f96', '#2f54eb'];

// Filters live in the URL so a filtered report can be shared as a link.
// Relative presets are kept as the preset name; only custom and FY ranges carry dates.
const URL_FILTERS = {
  preset: { type: 'string', default: 'all' },
  range: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  source: { type: 'string', default: 'all' },
};
const PRESETS_WITH_DATES = ['custom', 'this_fy', 'last_fy'];
// Relative presets are recomputed from the name; a custom range with no dates is empty.
const resolveRange = (preset, range) =>
  (PRESETS_WITH_DATES.includes(preset) && range) || (preset === 'custom' ? null : getDateRangeFromPreset(preset));

const LeadConversionReport = () => {
  const isMobile = useIsMobile();
  const reportRef = useRef(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const filterPreset = filters.preset;
  const dateRange = useMemo(() => resolveRange(filterPreset, filters.range), [filterPreset, filters.range]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const sourceFilter = filters.source;
  const setSourceFilter = (value) => setFilters({ source: value });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const useDateRange = filterPreset === 'custom' || filterPreset === 'this_fy' || filterPreset === 'last_fy';
      const params = {
        filter_preset: useDateRange ? undefined : (filterPreset === 'all' ? 'all_time' : filterPreset),
        start_date: useDateRange && dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: useDateRange && dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      };

      if (sourceFilter !== 'all') {
        params.source = sourceFilter;
      }

      const response = await axios.get('/lead/leads/conversion-analytics/', { params });

      if (response.status === 200) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to fetch lead conversion data');
    } finally {
      setLoading(false);
    }
  }, [filterPreset, dateRange, sourceFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle preset change
  const handlePresetChange = (preset) => {
    setFilters({
      preset,
      range: PRESETS_WITH_DATES.includes(preset) ? getDateRangeFromPreset(preset) : null,
    });
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({ preset: 'custom', range: [dates[0], dates[1]] });
    } else {
      setFilters({ preset: 'all', range: null });
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({ preset: 'all', range: null });
    setSourceFilter('all');
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filterPreset !== 'all') count++;
    if (sourceFilter !== 'all') count++;
    return count;
  };

  const columns = [
    {
      title: 'Lead #',
      dataIndex: 'lead_number',
      key: 'lead_number',
      sorter: (a, b) => (a.lead_number || '').localeCompare(b.lead_number || ''),
      width: 100,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      render: (val) => val || 'N/A',
      width: 120,
      ellipsis: true,
      responsive: ['md'],
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (source) => <Tag color="blue">{source}</Tag>,
      width: 100,
      responsive: ['lg'],
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      width: 100,
      responsive: ['md'],
    },
    {
      title: 'Status',
      dataIndex: 'is_converted',
      key: 'is_converted',
      render: (isConverted) => (
        <Tag color={isConverted ? 'green' : 'orange'}>
          {isConverted ? 'Converted' : 'Open'}
        </Tag>
      ),
      filters: [
        { text: 'Converted', value: true },
        { text: 'Open', value: false },
      ],
      onFilter: (value, record) => record.is_converted === value,
      width: 100,
    },
    {
      title: 'Converted Date',
      dataIndex: 'converted_at',
      key: 'converted_at',
      render: (val) => val || '-',
      width: 110,
      responsive: ['lg'],
    },
    {
      title: 'Days to Convert',
      dataIndex: 'days_to_convert',
      key: 'days_to_convert',
      render: (val) => val !== null ? `${val} days` : '-',
      sorter: (a, b) => (a.days_to_convert || 999) - (b.days_to_convert || 999),
      align: 'right',
      width: 100,
      responsive: ['md'],
    },
    {
      title: 'Value',
      dataIndex: 'converted_value',
      key: 'converted_value',
      render: (val) => val > 0 ? `₹${val?.toLocaleString('en-IN')}` : '-',
      sorter: (a, b) => a.converted_value - b.converted_value,
      align: 'right',
      width: 100,
    },
  ];

  // Source performance table columns with responsive config
  const sourcePerformanceColumns = [
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (s) => <Tag color="blue">{s}</Tag>,
      width: 100,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      width: 70,
    },
    {
      title: 'Converted',
      dataIndex: 'converted',
      key: 'converted',
      align: 'right',
      width: 90,
      responsive: ['sm'],
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      render: (r) => <Tag color={r >= 30 ? 'green' : r >= 15 ? 'orange' : 'red'}>{r}%</Tag>,
      align: 'right',
      width: 80,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (v) => `₹${v?.toLocaleString('en-IN')}`,
      align: 'right',
      width: 100,
      responsive: ['md'],
    },
  ];

  // Prepare pie chart data
  const pieData = data?.by_source?.map((item, index) => ({
    name: item.source,
    value: item.total,
    converted: item.converted,
    fill: COLORS[index % COLORS.length]
  })) || [];

  // Render additional filters for modal
  const renderAdditionalFilters = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Source</Text>
        <Select
          value={sourceFilter}
          onChange={setSourceFilter}
          style={{ width: '100%' }}
          placeholder="Select Source"
        >
          <Option value="all">All Sources</Option>
          {data?.available_sources?.map(source => (
            <Option key={source} value={source}>{source}</Option>
          ))}
        </Select>
      </div>
    </Space>
  );

  // Tables for the PDF export. Passed explicitly rather than scraped from the
  // rendered tables so the export carries every row, not just the page on screen.
  const pdfTables = [
    {
      title: 'Source Performance',
      columns: [
        { header: 'Source', key: 'source' },
        { header: 'Total', key: 'total', align: 'right' },
        { header: 'Converted', key: 'converted', align: 'right' },
        { header: 'Rate', key: 'rate', align: 'right', format: 'percent' },
        { header: 'Value', key: 'value', align: 'right', format: 'currency' },
      ],
      rows: data?.by_source || [],
    },
    {
      title: 'Lead Details',
      columns: [
        { header: 'Lead #', key: 'lead_number' },
        { header: 'Name', key: 'name' },
        { header: 'Company', key: 'company' },
        { header: 'Source', key: 'source' },
        { header: 'Created', key: 'created_at' },
        { header: 'Status', key: 'is_converted', format: (value) => (value ? 'Converted' : 'Open') },
        { header: 'Converted Date', key: 'converted_at' },
        { header: 'Days to Convert', key: 'days_to_convert', align: 'right' },
        { header: 'Value', key: 'converted_value', align: 'right', format: 'currency' },
      ],
      rows: data?.leads || [],
    },
  ];

  const pdfMeta = [
    { label: 'Total Leads', value: data?.summary?.total_leads || 0 },
    { label: 'Converted', value: data?.summary?.converted_leads || 0 },
    { label: 'Conversion Rate', value: `${data?.summary?.conversion_rate || 0}%` },
    { label: 'Converted Value', value: `Rs. ${(data?.summary?.total_converted_value || 0).toLocaleString('en-IN')}` },
  ];

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>Lead Conversion Report</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ExportPdfButton
            reportRef={reportRef}
            title="Lead Conversion Report"
            subtitle={data?.summary?.date_range}
            meta={pdfMeta}
            tables={pdfTables}
            fileName="lead-conversion-report"
            orientation="landscape"
            disabled={loading || !data}
          />
          {isMobile && (
            <MobileFilterButton
              onClick={() => setFilterModalVisible(true)}
              activeFiltersCount={getActiveFiltersCount()}
            />
          )}
        </div>
      </div>

      {/* Desktop Filters */}
      {!isMobile && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Date Range:</Text>
              <DatePresetButtons filterPreset={filterPreset} onPresetChange={handlePresetChange} />
            </div>
            <Space wrap size="middle">
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Custom Date Range:</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  size="small"
                  allowClear={true}
                  placeholder={['Start Date', 'End Date']}
                />
              </div>
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Source:</Text>
                <Select
                  value={sourceFilter}
                  onChange={setSourceFilter}
                  size="small"
                  style={{ width: 180 }}
                >
                  <Option value="all">All Sources</Option>
                  {data?.available_sources?.map(source => (
                    <Option key={source} value={source}>{source}</Option>
                  ))}
                </Select>
              </div>
            </Space>
          </Space>
        </Card>
      )}

      {/* Mobile Filter Modal */}
      <ReportFilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        filterPreset={filterPreset}
        dateRange={dateRange}
        onChange={({ preset, range }) => setFilters({
          preset,
          range: PRESETS_WITH_DATES.includes(preset) ? range : null,
        })}
        onClearFilters={handleClearFilters}
        title="Lead Conversion Filters"
      >
        {renderAdditionalFilters()}
      </ReportFilterModal>

      {error && (
        <Alert message="Error" description={error} type="error" closable showIcon onClose={() => setError(null)} style={{ marginBottom: '16px' }} />
      )}

      {loading && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}><Text>Loading lead conversion data...</Text></div>
          </div>
        </Card>
      )}

      {!loading && data && (
        <>
          {/* Summary Statistics */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} sm={12} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Leads</span>}
                  value={data.summary?.total_leads || 0}
                  prefix={<UserOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Converted</span>}
                  value={data.summary?.converted_leads || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Conversion Rate</span>}
                  value={data.summary?.conversion_rate || 0}
                  suffix="%"
                  prefix={<PercentageOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Avg Days to Convert</span>}
                  value={data.summary?.avg_conversion_days || 0}
                  suffix={isMobile ? 'd' : 'days'}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Converted Value</span>}
                  value={data.summary?.total_converted_value || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            {/* Monthly Trend Chart */}
            <Col xs={24} md={14}>
              <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Monthly Conversion Trend</Text>}>
                {data.monthly_trend && data.monthly_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
                    <LineChart data={data.monthly_trend} margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: isMobile ? 9 : 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        tick={{ fontSize: isMobile ? 9 : 11 }}
                        width={isMobile ? 30 : 40}
                      />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: isMobile ? '11px' : '12px' }} />
                      <Line type="monotone" dataKey="total" stroke="#1890ff" strokeWidth={2} name="Total Leads" dot={{ r: isMobile ? 2 : 3 }} />
                      <Line type="monotone" dataKey="converted" stroke="#52c41a" strokeWidth={2} name="Converted" dot={{ r: isMobile ? 2 : 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: isMobile ? '20px' : '40px' }}><Text type="secondary">No trend data available</Text></div>
                )}
              </Card>
            </Col>

            {/* Leads by Source Pie Chart */}
            <Col xs={24} md={10}>
              <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Leads by Source</Text>}>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? 35 : 50}
                        outerRadius={isMobile ? 65 : 90}
                        paddingAngle={2}
                        dataKey="value"
                        label={isMobile ? false : ({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={isMobile ? false : { length: 10, length2: 5 }}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name, props) => [`${value} leads (${props.payload.converted} converted)`, props.payload.name]} />
                      {isMobile && <Legend wrapperStyle={{ fontSize: '10px' }} />}
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: isMobile ? '20px' : '40px' }}><Text type="secondary">No source data available</Text></div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Source Performance Table */}
          {data.by_source && data.by_source.length > 0 && (
            <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Source Performance</Text>} style={{ marginBottom: '16px' }}>
              <Table
                dataSource={data.by_source}
                rowKey="source"
                size="small"
                pagination={false}
                columns={sourcePerformanceColumns}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          )}

          {/* Leads Detail Table */}
          <Card size="small" title={
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
              <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Lead Details</Text>
              {!isMobile && (
                <Text type="secondary" style={{ fontSize: '12px' }}>{data.summary?.date_range}</Text>
              )}
            </div>
          }>
            <Table
              columns={columns}
              dataSource={data.leads || []}
              rowKey="id"
              size="small"
              pagination={{
                defaultPageSize: isMobile ? 5 : 10,
                pageSizeOptions: ['5', '10', '20', '50'],
                showSizeChanger: !isMobile,
                showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total}`,
                simple: isMobile,
              }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      )}

      {!loading && !data && !error && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">No data available. Please adjust filters.</Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default LeadConversionReport;
